import React, { useState, useEffect, useCallback } from 'react';
import { FaGithub, FaLinkedin, FaFacebook, FaEnvelope } from 'react-icons/fa';
import profilePhoto from '../assets/images/profile-photo.jpg';

// Constants for scroll behavior
const SCROLL_OFFSET = 100;
const BOTTOM_THRESHOLD = 50;
const SCROLL_THROTTLE_MS = 100;

// Throttle utility function
const throttle = (func, delay) => {
    let timeoutId;
    let lastRan;
    return function (...args) {
        if (!lastRan) {
            func.apply(this, args);
            lastRan = Date.now();
        } else {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                if (Date.now() - lastRan >= delay) {
                    func.apply(this, args);
                    lastRan = Date.now();
                }
            }, delay - (Date.now() - lastRan));
        }
    };
};

const Header = () => {
    const [activeSection, setActiveSection] = useState('about');
    const [imageError, setImageError] = useState(false);
    const [showResumeMsg, setShowResumeMsg] = useState(false);

    // Get social links from environment variables
    const githubUrl = process.env.REACT_APP_GITHUB_URL || 'https://github.com/xyz-hello';
    const linkedinUrl = process.env.REACT_APP_LINKEDIN_URL || 'https://www.linkedin.com/in/palahuddinmuksan';
    const facebookUrl = process.env.REACT_APP_FACEBOOK_URL || 'https://www.facebook.com/tokioct/';
    const emailAddress = process.env.REACT_APP_EMAIL || 'palahuddinalkhalid@gmail.com';

    // Update active section based on URL hash on mount
    useEffect(() => {
        const hash = window.location.hash.slice(1);
        if (hash) {
            setActiveSection(hash);
            const element = document.getElementById(hash);
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth' });
                }, 100);
            }
        }
    }, []);

    // Listen for scroll events and update the active navigation section based on scroll position
    useEffect(() => {
        const handleScroll = () => {
            const sections = ['about', 'experience', 'projects', 'contact'];
            const scrollPosition = window.scrollY + SCROLL_OFFSET;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;

            // If near the bottom, set Contact as active
            if (scrollPosition + windowHeight >= documentHeight - BOTTOM_THRESHOLD) {
                setActiveSection('contact');
                window.history.replaceState(null, '', '#contact');
                return;
            }

            // Find the section currently in view
            for (let i = sections.length - 1; i >= 0; i--) {
                const section = sections[i];
                const element = document.getElementById(section);
                if (element) {
                    const offsetTop = element.offsetTop;
                    if (scrollPosition >= offsetTop - BOTTOM_THRESHOLD) {
                        setActiveSection(section);
                        window.history.replaceState(null, '', `#${section}`);
                        break;
                    }
                }
            }
        };

        // Throttle scroll handler for better performance
        const throttledHandleScroll = throttle(handleScroll, SCROLL_THROTTLE_MS);

        window.addEventListener('scroll', throttledHandleScroll);
        return () => window.removeEventListener('scroll', throttledHandleScroll);
    }, []);


    // Smoothly scroll to a section when a nav link is clicked
    const scrollToSection = useCallback((sectionId, event) => {
        if (event) {
            event.preventDefault();
        }
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            window.history.pushState(null, '', `#${sectionId}`);

            // Set focus to section for accessibility
            setTimeout(() => {
                const heading = element.querySelector('h2, h1');
                if (heading) {
                    heading.setAttribute('tabindex', '-1');
                    heading.focus();
                }
            }, 500);
        }
    }, []);

    const handleImageError = useCallback(() => {
        setImageError(true);
    }, []);

    const handleResumeClick = useCallback((e) => {
        e.preventDefault();
        setShowResumeMsg(true);
        setTimeout(() => setShowResumeMsg(false), 3000);
    }, []);

    return (
        <div className="header-content">
            <div>
                <div className="profile-photo-container">
                    {!imageError && (
                        <img
                            src={profilePhoto}
                            alt="Al Khalid M. Palahuddin"
                            className="profile-photo"
                            onError={handleImageError}
                            loading="eager"
                        />
                    )}
                </div>
                {imageError && <h1 className="name">Al Khalid M. Palahuddin</h1>}
                <h2 className="title">Computer Engineering</h2>
                <p className="description">
                    Hi, I'm Al Khalid M. Palahuddin 👋
                    <br />
                    Currently based in the Philippines.
                </p>

                <div className="header-social-links">
                    <a href={githubUrl} className="header-social-link" target="_blank" rel="noopener noreferrer" aria-label="GitHub Profile">
                        <FaGithub aria-hidden="true" />
                    </a>
                    <a href={linkedinUrl} className="header-social-link" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn Profile">
                        <FaLinkedin aria-hidden="true" />
                    </a>
                    <a href={facebookUrl} className="header-social-link" target="_blank" rel="noopener noreferrer" aria-label="Facebook Profile">
                        <FaFacebook aria-hidden="true" />
                    </a>
                    <a href={`mailto:${emailAddress}`} className="header-social-link" aria-label="Email">
                        <FaEnvelope aria-hidden="true" />
                    </a>
                </div>

            </div>

            <nav aria-label="Main navigation">
                <ul className="nav-links">
                    <li>
                        <a
                            href="#about"
                            className={`nav-link ${activeSection === 'about' ? 'active' : ''}`}
                            onClick={(e) => scrollToSection('about', e)}
                            aria-current={activeSection === 'about' ? 'page' : undefined}
                        >
                            About
                        </a>
                    </li>
                    <li>
                        <a
                            href="#experience"
                            className={`nav-link ${activeSection === 'experience' ? 'active' : ''}`}
                            onClick={(e) => scrollToSection('experience', e)}
                            aria-current={activeSection === 'experience' ? 'page' : undefined}
                        >
                            Experience
                        </a>
                    </li>
                    <li>
                        <a
                            href="#projects"
                            className={`nav-link ${activeSection === 'projects' ? 'active' : ''}`}
                            onClick={(e) => scrollToSection('projects', e)}
                            aria-current={activeSection === 'projects' ? 'page' : undefined}
                        >
                            Projects
                        </a>
                    </li>
                    <li>
                        <a
                            href="#contact"
                            className={`nav-link ${activeSection === 'contact' ? 'active' : ''}`}
                            onClick={(e) => scrollToSection('contact', e)}
                            aria-current={activeSection === 'contact' ? 'page' : undefined}
                        >
                            Contact
                        </a>
                    </li>
                    <li style={{ position: 'relative' }}>
                        <a
                            href="/my_cv_2026.pdf"
                            className="nav-link resume-link"
                            rel="noopener noreferrer"
                            aria-label="View Resume"
                            onClick={handleResumeClick}
                        >
                            Resume
                        </a>
                        {showResumeMsg && (
                            <span className="resume-tooltip">Available upon request</span>
                        )}
                    </li>
                </ul>
            </nav>
        </div>
    );
};

export default Header;
